import { useState, useCallback } from 'react';
import { CircularProgress } from '../CircularProgress';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  KPICell, SectionFrame, performanceColor,
  type PeriodContentProps, type DashboardFilterScope,
} from './shared';
import { useQualityMeasureLabel } from '../../../lib/qualityLabels';
import {
  CurrentWeekDrilldownModal,
  type CurrentWeekDrilldownMode,
} from './CurrentWeekDrilldownModal';
import {
  CurrentWeekNCRDrilldownModal,
  type NCRDrilldownMode,
} from './CurrentWeekNCRDrilldownModal';
import { YTDDrilldownModal } from './YTDDrilldownModal';

const PALETTE = {
  onTime: '#0f766e',
  early:  '#2563eb',
  late:   '#dc2626',
  pending: '#cbd5e1',
};

/** Map a weekday short label (Sun..Sat) to an ISO date inside the
 *  current week. ``window_start`` is the **Monday** of the current week
 *  (see ``_monday_of`` in scheduled_dashboard_service). Mon=0, Sun=6.
 *  Saturday and Sunday belong to the END of the same week. */
const DAY_OFFSETS: Record<string, number> = {
  Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6,
};
function dayIsoFor(weekStartIso: string, day: string): string {
  const offset = DAY_OFFSETS[day] ?? 0;
  const d = new Date(weekStartIso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + offset);
  return d.toISOString().slice(0, 10);
}

/** Default filter scope when ``filters`` is undefined (e.g. unit
 *  tests). The shell threads it through ScheduledTab in production. */
const DEFAULT_FILTERS: DashboardFilterScope = { erp_order_type: '4' };

export function CurrentWeek({ data, formatCurrency, filters }: PeriodContentProps) {
  const cw = data.current_week;
  const qualityLabel = useQualityMeasureLabel();
  const scope = filters ?? DEFAULT_FILTERS;

  // Mirror the gating used by DefaultTab / SiteKPITab so the customer's
  // PerformanceSettings.delivery_metric_mode controls whether OTIF, OTD,
  // or both blocks appear here too.
  const deliveryMode = data.delivery_metric_mode ?? 'both';
  const showOTIF = deliveryMode === 'both' || deliveryMode === 'otif_only';
  const showOTD  = deliveryMode === 'both' || deliveryMode === 'otd_only';

  const weekStartIso = cw.window_start;
  const weekEndIso   = cw.window_end;
  const otifStartIso = cw.otif_window_start;
  const otifEndIso   = cw.otif_window_end;
  const todayIso     = data.today;

  const [scheduleMode, setScheduleMode] = useState<CurrentWeekDrilldownMode | null>(null);
  const [scheduleDayIso, setScheduleDayIso] = useState<string | undefined>(undefined);
  const [ncrMode, setNcrMode] = useState<NCRDrilldownMode | null>(null);
  const [appealsOpen, setAppealsOpen] = useState(false);

  const openSchedule = useCallback((mode: CurrentWeekDrilldownMode, dayIso?: string) => {
    setScheduleDayIso(dayIso);
    setScheduleMode(mode);
  }, []);
  const closeSchedule = useCallback(() => {
    setScheduleMode(null);
    setScheduleDayIso(undefined);
  }, []);
  const openNcr = useCallback((mode: NCRDrilldownMode) => setNcrMode(mode), []);
  const closeNcr = useCallback(() => setNcrMode(null), []);

  const deliveriesByDay = cw.deliveries_by_day.map((d) => ({
    day: d.day,
    onTime: d.on_time,
    early: d.early,
    late: d.late,
    pending: d.pending,
  }));

  const otdSplit = [
    { name: 'On time', value: cw.delivery.on_time, color: PALETTE.onTime, mode: 'mix_on_time' as const },
    { name: 'Early',   value: cw.delivery.early,   color: PALETTE.early,  mode: 'mix_early'   as const },
    { name: 'Late',    value: cw.delivery.late,    color: PALETTE.late,   mode: 'mix_late'    as const },
  ];
  const otdEmpty = otdSplit.every(d => d.value === 0);
  const otdData: Array<{ name: string; value: number; color: string; mode: CurrentWeekDrilldownMode | null }> = otdEmpty
    ? [{ name: 'No data yet', value: 1, color: '#f1f5f9', mode: null }]
    : otdSplit;

  // OTIF Mix — three outcomes only:
  //   Passed  = ``passed``  (delivered in full within OTIF tolerance)
  //   Pending = ``pending`` (still within tolerance window, supplier
  //                          still has time)
  //   Failed  = ``failed``  (past window without passing, OR delivered
  //                          but didn't meet OTIF — short or out of
  //                          window). No "On-time short" / "Late in-full"
  //                          slices — both collapse into Failed.
  const otifPass    = cw.delivery.passed  ?? 0;
  const otifPending = cw.delivery.pending ?? 0;
  const otifFail    = cw.delivery.failed  ?? 0;
  const otifSplit = [
    { name: 'Passed',  value: otifPass,    color: PALETTE.onTime, mode: 'mix_otif_pass'    as const },
    { name: 'Pending', value: otifPending, color: '#94a3b8',      mode: 'mix_otif_pending' as const },
    { name: 'Failed',  value: otifFail,    color: '#dc2626',      mode: 'mix_otif_fail'    as const },
  ];
  const otifEmpty = otifSplit.every(d => d.value === 0);
  const otifData: Array<{ name: string; value: number; color: string; mode: CurrentWeekDrilldownMode | null }> = otifEmpty
    ? [{ name: 'No data yet', value: 1, color: '#f1f5f9', mode: null }]
    : otifSplit;

  // Per-segment click handler. Recharts ``BarChart.onClick`` fires once
  // for the whole chart and gives ``activePayload[0]`` (always the first
  // series), so it can't distinguish which segment of a stack was
  // clicked. Wire ``<Bar onClick>`` on each Bar instead — the payload is
  // the data row (so we can read ``day``) and we already know the
  // dataKey/segment at the call site.
  const onSegmentClick = (segment: 'onTime' | 'early' | 'late' | 'pending') => (raw: unknown) => {
    const dayRaw = raw && typeof raw === 'object'
      ? (raw as { payload?: { day?: unknown }; day?: unknown }).day
        ?? (raw as { payload?: { day?: unknown } }).payload?.day
      : undefined;
    if (typeof dayRaw !== 'string') return;
    const iso = dayIsoFor(weekStartIso, dayRaw);
    if (segment === 'onTime')      openSchedule('day_on_time', iso);
    else if (segment === 'early')   openSchedule('day_early',   iso);
    else if (segment === 'late')    openSchedule('day_late',    iso);
    else if (segment === 'pending') openSchedule('day_pending', iso);
  };

  const handleSliceClick = (slice: unknown) => {
    if (!slice || typeof slice !== 'object') return;
    const m = (slice as { mode?: unknown }).mode;
    if (typeof m === 'string') openSchedule(m as CurrentWeekDrilldownMode);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <SectionFrame title="Quality" accent="red" subtitle="Sun → today">
          <div className="grid grid-cols-2 gap-3">
            <KPICell
              label={qualityLabel}
              value={<span className="text-2xl font-semibold tabular-nums">{cw.quality.ppm.toLocaleString()}</span>}
              foot={`${cw.quality.reject_qty.toLocaleString()} rej`}
              onClick={() => openNcr('ppm')}
            />
            <KPICell
              label="NCRs"
              value={<span className="text-2xl font-semibold tabular-nums">{cw.quality.ncrs.toLocaleString()}</span>}
              onClick={() => openNcr('ncrs')}
            />
          </div>
        </SectionFrame>

        <SectionFrame title="Delivery" accent="green" subtitle={cw.frozen ? 'Frozen at Thu 17:00' : 'Sun → today'}>
          <div
            className={`grid gap-3 items-start ${
              showOTIF && showOTD ? 'grid-cols-3' : 'grid-cols-2'
            }`}
          >
            {showOTIF && (
              <KPICell
                label="OTIF"
                value={<CircularProgress percentage={cw.delivery.otif_pct} size={68} strokeWidth={6} color={performanceColor(cw.delivery.otif_pct)} />}
                foot={`${cw.delivery.otif_met}/${cw.delivery.total_due} schedules`}
                onClick={() => openSchedule('otif_this_week')}
              />
            )}
            {showOTD && (
              <KPICell
                label="OTD"
                value={<CircularProgress percentage={cw.delivery.otd_pct} size={68} strokeWidth={6} color={performanceColor(cw.delivery.otd_pct)} />}
                foot={`${cw.delivery.otd_met}/${cw.delivery.total_due} within firm`}
                onClick={() => openSchedule('otd_this_week')}
              />
            )}
            <KPICell
              label="Appeals"
              value={<span className="text-2xl font-semibold tabular-nums">{cw.delivery.appeals}</span>}
              onClick={() => setAppealsOpen(true)}
            />
          </div>
        </SectionFrame>

        <SectionFrame title="Cost" accent="blue" subtitle="schedules due this week">
          <div className="grid grid-cols-3 gap-3">
            <KPICell
              label="Open Schedule Lines"
              value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(cw.cost.open_value)}</span>}
              foot={`${cw.cost.open_lines} of ${cw.cost.total_lines} lines`}
              onClick={() => openSchedule('open_this_week')}
            />
            <KPICell
              label="Received"
              value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(cw.cost.received_value)}</span>}
              foot={`of ${formatCurrency(cw.cost.total_value)} committed`}
              onClick={() => openSchedule('received_this_week')}
            />
            <KPICell
              label="Arrears"
              value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(cw.cost.arrears_value)}</span>}
              foot={`${cw.cost.arrears_lines} past-due lines`}
              onClick={() => openSchedule('arrears')}
            />
          </div>
        </SectionFrame>
      </div>

      {/* Deliveries by Day + OTD Mix + OTIF Mix.
          Layout collapses to fewer columns when OTD or OTIF is hidden by the
          delivery_metric_mode setting. */}
      <div className={`grid gap-4 ${
        showOTIF && showOTD ? 'grid-cols-3'
        : (showOTIF || showOTD) ? 'grid-cols-2'
        : 'grid-cols-1'
      }`}>
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">Deliveries by Day</CardTitle>
            <p className="text-[11px] text-muted-foreground">Sunday through Saturday • this week — click a segment to drill in</p>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={deliveriesByDay} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  {/* Darker hover cursor + activeBar outline so the user sees
                      the whole day's column light up before clicking a
                      segment. */}
                  <Tooltip cursor={{ fill: '#e2e8f0', fillOpacity: 0.7 }} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                  <Bar dataKey="onTime"  stackId="a" fill={PALETTE.onTime} name="On time" cursor="pointer" activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} onClick={onSegmentClick('onTime')} />
                  <Bar dataKey="early"   stackId="a" fill={PALETTE.early}  name="Early"   cursor="pointer" activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} onClick={onSegmentClick('early')} />
                  <Bar dataKey="late"    stackId="a" fill={PALETTE.late}   name="Late"    cursor="pointer" activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} onClick={onSegmentClick('late')} />
                  <Bar dataKey="pending" stackId="a" fill={PALETTE.pending} name="Pending" radius={[3, 3, 0, 0]} cursor="pointer" activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} onClick={onSegmentClick('pending')} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {showOTD && (
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-700">OTD Mix</CardTitle>
              <p className="text-[11px] text-muted-foreground">Week to date • {cw.delivery.total_with_status.toLocaleString()} attributed deliveries — click a slice to drill in</p>
            </CardHeader>
            <CardContent>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={otdData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%" cy="50%"
                      innerRadius={56}
                      outerRadius={84}
                      paddingAngle={otdEmpty ? 0 : 2}
                      stroke="none"
                      onClick={handleSliceClick}
                      activeShape={{ stroke: '#0f172a', strokeWidth: 1.5 } as unknown as Record<string, unknown>}
                    >
                      {otdData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} cursor={entry.mode ? 'pointer' : 'default'} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {showOTIF && (
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-700">OTIF Mix</CardTitle>
              <p className="text-[11px] text-muted-foreground">This week • {(otifPass + otifPending + otifFail).toLocaleString()} schedules — click a slice to drill in</p>
            </CardHeader>
            <CardContent>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={otifData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%" cy="50%"
                      innerRadius={56}
                      outerRadius={84}
                      paddingAngle={otifEmpty ? 0 : 2}
                      stroke="none"
                      onClick={handleSliceClick}
                      activeShape={{ stroke: '#0f172a', strokeWidth: 1.5 } as unknown as Record<string, unknown>}
                    >
                      {otifData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} cursor={entry.mode ? 'pointer' : 'default'} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {scheduleMode && (
        <CurrentWeekDrilldownModal
          open={scheduleMode !== null}
          onClose={closeSchedule}
          mode={scheduleMode}
          filters={scope}
          weekStartIso={weekStartIso}
          weekEndIso={weekEndIso}
          otifWindowStartIso={otifStartIso}
          otifWindowEndIso={otifEndIso}
          todayIso={todayIso}
          dayIso={scheduleDayIso}
        />
      )}
      {ncrMode && (
        <CurrentWeekNCRDrilldownModal
          open={ncrMode !== null}
          onClose={closeNcr}
          mode={ncrMode}
          filters={scope}
          weekStartIso={weekStartIso}
          weekEndIso={weekEndIso}
        />
      )}

      <YTDDrilldownModal
        open={appealsOpen}
        onClose={() => setAppealsOpen(false)}
        mode="delivery_appeals"
        filters={scope}
        periodStart={weekStartIso}
        periodEnd={weekEndIso}
        todayIso={todayIso}
        periodLabel="This week"
      />
    </div>
  );
}
